/**
 * Locale-aware formatting (REQ-16) through `Intl`, never hand-rolled. The FORMAT locale may
 * differ from the interface language (a player reading English UI with Spanish number
 * formatting), which is why every helper takes it explicitly; `useI18n()` supplies it.
 */
import { DEFAULT_LOCALE, PSEUDO_LOCALE } from "@shared/i18n/localeConfig";

/** The pseudolocale formats as English: it fakes text, not number conventions. */
export function intlLocale(locale: string): string {
  return locale === PSEUDO_LOCALE ? DEFAULT_LOCALE : locale;
}

export interface Formatters {
  number(value: number, options?: Intl.NumberFormatOptions): string;
  /** `0.25` → `25%` (en), `25 %` (fr, de). */
  percent(fraction: number, options?: Intl.NumberFormatOptions): string;
  date(value: Date | number, options?: Intl.DateTimeFormatOptions): string;
  time(value: Date | number, options?: Intl.DateTimeFormatOptions): string;
  /** `relativeTime(-3, "day")` → `3 days ago`. */
  relativeTime(value: number, unit: Intl.RelativeTimeFormatUnit, options?: Intl.RelativeTimeFormatOptions): string;
  currency(value: number, currency: string, options?: Intl.NumberFormatOptions): string;
}

export function createFormatters(locale: string): Formatters {
  const tag = intlLocale(locale);
  return {
    number: (value, options) => new Intl.NumberFormat(tag, options).format(value),
    percent: (fraction, options) => new Intl.NumberFormat(tag, { style: "percent", ...options }).format(fraction),
    date: (value, options) => new Intl.DateTimeFormat(tag, options ?? { dateStyle: "medium" }).format(value),
    time: (value, options) => new Intl.DateTimeFormat(tag, options ?? { timeStyle: "short" }).format(value),
    relativeTime: (value, unit, options) =>
      new Intl.RelativeTimeFormat(tag, { numeric: "auto", ...options }).format(value, unit),
    currency: (value, currency, options) =>
      new Intl.NumberFormat(tag, { style: "currency", currency, ...options }).format(value),
  };
}
